import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, CalendarClock, CalendarX2, Copy, History, Pencil, RotateCcw, Send, Trash2, UserMinus, UserPlus, Users } from "lucide-react";
import { academicTermsApi, type AcademicTerm } from "../../api/academics";
import Modal from "../ui/Modal";
import { useConfirm } from "../../contexts/ConfirmContext";
import { useToast } from "../../contexts/ToastContext";
import {
  apiError,
  formatYmd,
  humanize,
  invitationSpan,
  officeHoursApi,
  scheduleWeeks,
  shortDate,
  studentName,
  weekStartOf,
  type OfficeHoursWeek,
  type OfficeHourSchedule,
  type OfficeHourSession,
  type OfficeHoursConfig,
  type RosterRow,
} from "../../api/officeHours";
import ScheduleDrawer from "./ScheduleDrawer";
import StudentPicker from "./StudentPicker";
import RegisterSheet from "./RegisterSheet";
import WeekNavigator from "./WeekNavigator";
import { SessionRow } from "./OfficeHoursHub";
import {
  BandPill,
  Card,
  CardTitle,
  dangerBtn,
  EmptyState,
  inputCls,
  labelCls,
  Muted,
  primaryBtn,
  RatePill,
  secondaryBtn,
  Spinner,
} from "./ohUi";
import SelectField from "../ui/SelectField";

/** Did this invitation cover (part of) the week? Removed-before-it-started ones never ran. */
const coversWeek = (r: RosterRow, w: OfficeHoursWeek) =>
  r.effective_from <= r.effective_to && r.effective_from <= w.end && r.effective_to >= w.start && (r.status === "ACTIVE" || r.end_reason_code !== null);

/**
 * /office-hours/schedules/:id (plan §9.4): the students invited week by week
 * with each one's attendance, the session timeline, and the schedule's actions.
 */
const ScheduleDetail: React.FC = () => {
  const { id } = useParams();
  const scheduleId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [config, setConfig] = useState<OfficeHoursConfig | null>(null);
  const [schedule, setSchedule] = useState<OfficeHourSchedule | null>(null);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [sessions, setSessions] = useState<OfficeHourSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removing, setRemoving] = useState<RosterRow | null>(null);
  const [removeReason, setRemoveReason] = useState("GOAL_MET");
  const [removeNote, setRemoveNote] = useState("");
  const [cancelling, setCancelling] = useState<OfficeHourSession | null>(null);
  const [cancelReason, setCancelReason] = useState("TEACHER_ABSENT");
  const [cancelNote, setCancelNote] = useState("");
  const [registerFor, setRegisterFor] = useState<OfficeHourSession | null>(null);
  const [showEnded, setShowEnded] = useState(false);
  const [moving, setMoving] = useState<OfficeHourSession | null>(null);
  const [moveForm, setMoveForm] = useState({ date: "", start_time: "", end_time: "" });
  const [rollingOver, setRollingOver] = useState(false);
  const [terms, setTerms] = useState<AcademicTerm[]>([]);
  const [targetTerm, setTargetTerm] = useState<number | "">("");
  const [week, setWeek] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!rollingOver || terms.length) return;
    academicTermsApi
      .getAll()
      .then((r) => {
        const list = ((r.data as any).data ?? []) as AcademicTerm[];
        setTerms(list.filter((t) => t.academic_term_id !== schedule?.academic_term_id).sort((a, b) => String(b.start_date).localeCompare(String(a.start_date))));
      })
      .catch(() => setTerms([]));
  }, [rollingOver, terms.length, schedule?.academic_term_id]);

  const load = useCallback(async () => {
    try {
      const [c, d] = await Promise.all([officeHoursApi.config(), officeHoursApi.schedule(scheduleId)]);
      setConfig(c.data.data);
      setSchedule(d.data.data.schedule);
      setRoster(d.data.data.roster);
      setSessions(d.data.data.sessions);
    } catch (error) {
      showToast(apiError(error, "Couldn't load these office hours"), "error");
    } finally {
      setLoading(false);
    }
  }, [scheduleId, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = useMemo(() => roster.filter((r) => r.status === "ACTIVE"), [roster]);
  // Weekly invitations end on their own ("completed"): only real departures count as former students.
  const ended = useMemo(() => roster.filter((r) => r.status === "ENDED" && r.end_reason_code !== "COMPLETED" && r.effective_from <= r.effective_to), [roster]);
  const today = config?.today ?? "";
  const weeks = useMemo(() => (schedule ? scheduleWeeks(schedule) : []), [schedule]);
  useEffect(() => {
    if (!weeks.length || (week && weeks.some((w) => w.start === week))) return;
    const now = weekStartOf(today || weeks[0].start);
    setWeek((weeks.find((w) => w.start === now) ?? weeks.find((w) => w.start > now) ?? weeks[weeks.length - 1]).start);
  }, [weeks, week, today]);
  const currentWeek = weeks.find((w) => w.start === week) ?? null;
  const weekIndex = currentWeek ? weeks.indexOf(currentWeek) : -1;
  const previousWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
  const weekCounts = useMemo(() => new Map(weeks.map((w) => [w.start, roster.filter((r) => coversWeek(r, w)).length])), [weeks, roster]);
  const weekRows = useMemo(
    () => (currentWeek ? roster.filter((r) => coversWeek(r, currentWeek)).sort((a, b) => studentName(a.student).localeCompare(studentName(b.student))) : []),
    [roster, currentWeek],
  );
  const inWeek = new Set(weekRows.map((r) => r.student_id));
  const lastWeekRows = previousWeek ? roster.filter((r) => coversWeek(r, previousWeek) && !inWeek.has(r.student_id)) : [];
  const lastWeekIds = [...new Set(lastWeekRows.map((r) => r.student_id))];
  const weekOpen = Boolean(currentWeek && currentWeek.end >= today);
  // Removing from a future week keeps the weeks before it; this week: from the next session on.
  const removeFrom = currentWeek && currentWeek.start > today ? currentWeek.start : undefined;
  const past = sessions.filter((s) => s.session_date < today || s.state === "held" || s.state === "unmarked").reverse();
  const future = sessions.filter((s) => !past.includes(s));
  const live = schedule?.status === "ACTIVE" || schedule?.status === "DRAFT";

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      showToast(ok, "success");
      await load();
    } catch (error) {
      showToast(apiError(error), "error");
    }
  };

  const copyLastWeek = async () => {
    if (!schedule || !currentWeek || !lastWeekIds.length) return;
    setCopying(true);
    try {
      const r = await officeHoursApi.assign(schedule.schedule_id, { student_ids: lastWeekIds, effective_from: currentWeek.start, effective_to: currentWeek.end });
      const res = r.data.data;
      const missed = res.conflicts.length + res.over_capacity.length + res.ineligible.length;
      showToast(
        `${res.assigned.length} invited for the week of ${shortDate(currentWeek.start)}${missed ? ` · ${missed} could not come (busy, full or no longer eligible)` : ""}`,
        res.assigned.length ? "success" : "warning",
      );
      await load();
    } catch (error) {
      showToast(apiError(error, "Couldn't copy last week"), "error");
    } finally {
      setCopying(false);
    }
  };

  const endSchedule = async () => {
    if (!schedule) return;
    const ok = await confirm({
      title: "End these office hours?",
      message: `Future sessions are cancelled and ${active.length} student${active.length === 1 ? "" : "s"} are released and told. Past registers stay.`,
      tone: "danger",
      confirmText: "End office hours",
    });
    if (ok) await run(() => officeHoursApi.endSchedule(schedule.schedule_id), "Office hours ended");
  };

  const deleteSchedule = async () => {
    if (!schedule) return;
    const ok = await confirm({ title: "Delete these office hours?", message: "This cannot be undone.", tone: "danger", confirmText: "Delete" });
    if (!ok) return;
    try {
      await officeHoursApi.deleteSchedule(schedule.schedule_id);
      showToast("Deleted", "success");
      navigate("/office-hours?tab=schedules");
    } catch (error) {
      showToast(apiError(error), "error");
    }
  };

  if (loading) return <div className="mx-auto max-w-6xl px-4 py-6"><Spinner label="Loading office hours" /></div>;
  if (!schedule || !config) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6">
        <EmptyState title="Office hours not found" action={<Link to="/office-hours" className={secondaryBtn}>Back to office hours</Link>} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 px-4 py-6 sm:px-6">
      <Link to="/office-hours?tab=schedules" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:underline dark:text-blue-300">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Office hours
      </Link>

      <Card labelledBy="oh-detail-title" className="relative overflow-hidden">
        {schedule.subject_color && <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: schedule.subject_color }} aria-hidden />}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 id="oh-detail-title" className="text-2xl font-bold text-slate-900 dark:text-gray-50">{schedule.title}</h1>
            <Muted>
              {schedule.days_label} · {schedule.start_time}–{schedule.end_time}
              {schedule.location ? ` · ${schedule.location}` : ""}
              {schedule.subject_name ? ` · ${schedule.subject_name}` : ""}
            </Muted>
            <Muted className="text-xs">
              {formatYmd(schedule.effective_from, { year: true })} – {formatYmd(schedule.effective_to, { year: true })} · {humanize(schedule.purpose)} · run by{" "}
              {schedule.teacher_name ?? "—"}
            </Muted>
            <span className="mt-2 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-gray-800/40 dark:text-gray-200">
              {schedule.status === "ACTIVE" ? "Published" : schedule.status === "DRAFT" ? "Draft — students not told yet" : schedule.status === "ENDED" ? "Ended" : "Cancelled"}
            </span>
          </div>
          {!live && (
            <button type="button" className={secondaryBtn} onClick={() => setRollingOver(true)}>
              <Copy className="h-4 w-4" aria-hidden /> Copy to another term
            </button>
          )}
          {live && (
            <div className="flex flex-wrap gap-2">
              {schedule.status === "DRAFT" && (
                <button type="button" className={primaryBtn} onClick={() => run(() => officeHoursApi.publishSchedule(schedule.schedule_id), "Published — students notified")}>
                  <Send className="h-4 w-4" aria-hidden /> Publish
                </button>
              )}
              <button type="button" className={secondaryBtn} onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" aria-hidden /> Edit
              </button>
              <button type="button" className={secondaryBtn} onClick={() => setRollingOver(true)}>
                <Copy className="h-4 w-4" aria-hidden /> Copy to another term
              </button>
              <button type="button" className={dangerBtn} onClick={endSchedule}>
                <Ban className="h-4 w-4" aria-hidden /> End
              </button>
              {!sessions.some((s) => s.state === "held") && (
                <button type="button" className={dangerBtn} onClick={deleteSchedule} aria-label="Delete office hours">
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Card labelledBy="oh-roster" className="min-w-0 lg:col-span-3">
          <CardTitle
            id="oh-roster"
            icon={<Users className="h-4 w-4" aria-hidden />}
            action={
              currentWeek && (
                <span className="text-sm font-semibold tabular-nums text-slate-700 dark:text-gray-200">
                  {weekRows.length}/{schedule.capacity} this week
                </span>
              )
            }
          >
            Students by week
          </CardTitle>
          {currentWeek && week && (
            <div className="mb-4">
              <WeekNavigator weeks={weeks} value={week} onChange={setWeek} today={today} counts={weekCounts} capacity={schedule.capacity} />
            </div>
          )}
          {live && weekOpen && (
            <div className="mb-3 flex flex-wrap gap-2">
              <button type="button" className={primaryBtn} onClick={() => setPickerOpen(true)}>
                <UserPlus className="h-4 w-4" aria-hidden /> Invite for this week
              </button>
              {lastWeekIds.length > 0 && (
                <button type="button" className={secondaryBtn} onClick={copyLastWeek} disabled={copying}>
                  <History className="h-4 w-4" aria-hidden /> {copying ? "Inviting…" : `Bring back last week's ${lastWeekIds.length}`}
                </button>
              )}
            </div>
          )}
          {weekRows.length === 0 ? (
            <EmptyState
              title={currentWeek ? `Nobody invited for the week of ${shortDate(currentWeek.start)}` : "No students yet"}
              body={weekOpen ? (lastWeekIds.length ? "Bring back last week's group, or invite new students." : "Invite the students who should come this week.") : "This week is over."}
            />
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-slate-600 dark:text-gray-300">
                    <th scope="col" className="py-2 pr-3 font-semibold">Student</th>
                    <th scope="col" className="py-2 pr-3 font-semibold">Invited</th>
                    <th scope="col" className="py-2 pr-3 font-semibold">Attendance</th>
                    <th scope="col" className="py-2 pr-3 font-semibold"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                  {weekRows.map((r) => (
                    <tr key={r.assignment_id} className={r.status === "ENDED" ? "opacity-70" : undefined}>
                      <td className="py-2 pr-3">
                        <p className="font-semibold text-slate-900 dark:text-gray-100">{studentName(r.student)}</p>
                        <p className="text-xs text-slate-600 dark:text-gray-300">
                          {[r.student.class_group_name, r.reason_code ? humanize(r.reason_code) : null].filter(Boolean).join(" · ")}
                        </p>
                        {r.clash_note && <p className="text-xs text-amber-800 dark:text-amber-200">Also scheduled: {r.clash_note}</p>}
                      </td>
                      <td className="py-2 pr-3 text-xs text-slate-700 dark:text-gray-200">
                        <span className="whitespace-nowrap">{invitationSpan(r.effective_from, r.effective_to, schedule.effective_to)}</span>
                        {r.status === "ENDED" && r.end_reason_code !== "COMPLETED" && <span className="block text-rose-700 dark:text-rose-300">Left · {humanize(r.end_reason_code)}</span>}
                      </td>
                      <td className="py-2 pr-3">
                        {r.stats ? (
                          <div className="flex items-center gap-2">
                            <RatePill rate={r.stats.rate} /> <BandPill band={r.stats.band} />
                          </div>
                        ) : (
                          <span className="text-xs text-slate-600 dark:text-gray-300">—</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        {live && weekOpen && r.status === "ACTIVE" && (
                          <button
                            type="button"
                            onClick={() => setRemoving(r)}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 dark:text-rose-300 dark:hover:bg-rose-500/10"
                            aria-label={`Remove ${studentName(r.student)}`}
                          >
                            <UserMinus className="h-3.5 w-3.5" aria-hidden /> Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {ended.length > 0 && (
            <div className="mt-4">
              <button type="button" className="text-sm font-semibold text-blue-700 hover:underline dark:text-blue-300" onClick={() => setShowEnded((v) => !v)} aria-expanded={showEnded}>
                {showEnded ? "Hide" : "Show"} {ended.length} former student{ended.length === 1 ? "" : "s"}
              </button>
              {showEnded && (
                <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-gray-200">
                  {ended.map((r) => (
                    <li key={r.assignment_id}>
                      {studentName(r.student)} — left {r.ended_at ? formatYmd(String(r.ended_at).slice(0, 10)) : formatYmd(r.effective_to)} ({humanize(r.end_reason_code)})
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>

        <div className="min-w-0 space-y-5 lg:col-span-2">
          <Card labelledBy="oh-upcoming">
            <CardTitle id="oh-upcoming">Next sessions</CardTitle>
            {future.length === 0 ? (
              <Muted>No more sessions.</Muted>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-gray-700/30">
                {future.slice(0, 8).map((s) => (
                  <li key={s.session_id} className="flex items-center justify-between gap-2 py-2">
                    <div>
                      <p className={`text-sm font-semibold ${s.state === "cancelled" ? "text-slate-500 line-through dark:text-gray-400" : "text-slate-900 dark:text-gray-100"}`}>
                        {formatYmd(s.session_date)}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-gray-300">
                        {s.state === "cancelled" ? `Cancelled · ${humanize(s.cancel_reason)}` : `${s.start_time}–${s.end_time} · ${s.expected} expected`}
                        {s.host_teacher_id !== schedule.teacher_id && s.host_name ? ` · covered by ${s.host_name}` : ""}
                      </p>
                    </div>
                    {live &&
                      (s.state === "cancelled" ? (
                        s.cancel_reason !== "CLOSURE" && (
                          <button type="button" className={secondaryBtn} onClick={() => run(() => officeHoursApi.restoreSession(s.session_id), "Session restored")} aria-label={`Restore ${formatYmd(s.session_date)}`}>
                            <RotateCcw className="h-4 w-4" aria-hidden />
                          </button>
                        )
                      ) : s.state === "running" ? (
                        <button type="button" className={primaryBtn} onClick={() => setRegisterFor(s)}>Take register</button>
                      ) : (
                        <span className="flex gap-1">
                          <button type="button" className={secondaryBtn} onClick={() => { setMoving(s); setMoveForm({ date: s.session_date, start_time: s.start_time, end_time: s.end_time }); }} aria-label={`Move ${formatYmd(s.session_date)}`}>
                            <CalendarClock className="h-4 w-4" aria-hidden />
                          </button>
                          <button type="button" className={secondaryBtn} onClick={() => setCancelling(s)} aria-label={`Cancel ${formatYmd(s.session_date)}`}>
                            <CalendarX2 className="h-4 w-4" aria-hidden />
                          </button>
                        </span>
                      ))}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card labelledBy="oh-past">
            <CardTitle id="oh-past">Past sessions</CardTitle>
            {past.length === 0 ? (
              <Muted>None yet.</Muted>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-gray-700/30">
                {past.slice(0, 12).map((s) => (
                  <SessionRow key={s.session_id} s={s} onRegister={setRegisterFor} />
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <ScheduleDrawer open={editOpen} onClose={() => setEditOpen(false)} config={config} termId={schedule.academic_term_id} schedule={schedule} onSaved={() => void load()} />

      <Modal isOpen={pickerOpen} onClose={() => { setPickerOpen(false); void load(); }} title={`Invite students · ${schedule.title}`} size="3xl">
        <StudentPicker
          scheduleId={schedule.schedule_id}
          reasonCodes={config.reason_codes}
          onAssigned={() => void load()}
          schedule={schedule}
          initialWeek={week ?? undefined}
          today={config.today}
        />
      </Modal>

      <Modal isOpen={Boolean(removing)} onClose={() => setRemoving(null)} title={`Remove ${studentName(removing?.student)}`}>
        <div className="space-y-3">
          <div>
            <label htmlFor="oh-remove-reason" className={labelCls}>Why?</label>
            <SelectField id="oh-remove-reason" className={inputCls} value={removeReason} onChange={(e) => setRemoveReason(e.target.value)}>
              {config.end_reason_codes.map((c) => (
                <option key={c} value={c}>{humanize(c)}</option>
              ))}
            </SelectField>
          </div>
          <div>
            <label htmlFor="oh-remove-note" className={labelCls}>Note (optional)</label>
            <input id="oh-remove-note" className={inputCls} value={removeNote} onChange={(e) => setRemoveNote(e.target.value)} maxLength={500} />
          </div>
          <Muted className="text-xs">
            {removeFrom && removing && removing.effective_from < removeFrom
              ? `${studentName(removing.student)} leaves from the week of ${shortDate(removeFrom)} on; the weeks before are kept. `
              : ""}
            The student is told and becomes free for other teachers' office hours.
          </Muted>
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryBtn} onClick={() => setRemoving(null)}>Cancel</button>
            <button
              type="button"
              className={primaryBtn}
              onClick={async () => {
                if (!removing) return;
                await run(
                  () => officeHoursApi.removeAssignment(removing.assignment_id, { end_reason_code: removeReason, end_note: removeNote.trim() || undefined, ...(removeFrom ? { from: removeFrom } : {}) }),
                  "Student removed",
                );
                setRemoving(null);
                setRemoveNote("");
              }}
            >
              Remove student
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(cancelling)} onClose={() => setCancelling(null)} title={`Cancel ${cancelling ? formatYmd(cancelling.session_date) : ""}`}>
        <div className="space-y-3">
          <div>
            <label htmlFor="oh-cancel-reason" className={labelCls}>Reason</label>
            <SelectField id="oh-cancel-reason" className={inputCls} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
              {config.cancel_reasons.map((c) => (
                <option key={c} value={c}>{humanize(c)}</option>
              ))}
            </SelectField>
          </div>
          <div>
            <label htmlFor="oh-cancel-note" className={labelCls}>Message to students (optional)</label>
            <input id="oh-cancel-note" className={inputCls} value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} maxLength={255} />
          </div>
          <Muted className="text-xs">Students are told straight away. A cancelled session never counts against them.</Muted>
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryBtn} onClick={() => setCancelling(null)}>Keep session</button>
            <button
              type="button"
              className={dangerBtn}
              onClick={async () => {
                if (!cancelling) return;
                await run(() => officeHoursApi.cancelSession(cancelling.session_id, cancelReason, cancelNote.trim() || undefined), "Session cancelled");
                setCancelling(null);
                setCancelNote("");
              }}
            >
              Cancel session
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={Boolean(moving)} onClose={() => setMoving(null)} title={`Move ${moving ? formatYmd(moving.session_date) : ""}`}>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor="oh-move-date" className={labelCls}>New date</label>
              <input id="oh-move-date" type="date" className={inputCls} value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} />
            </div>
            <div>
              <label htmlFor="oh-move-start" className={labelCls}>Starts</label>
              <input id="oh-move-start" type="time" className={inputCls} value={moveForm.start_time} onChange={(e) => setMoveForm({ ...moveForm, start_time: e.target.value })} />
            </div>
            <div>
              <label htmlFor="oh-move-end" className={labelCls}>Ends</label>
              <input id="oh-move-end" type="time" className={inputCls} value={moveForm.end_time} onChange={(e) => setMoveForm({ ...moveForm, end_time: e.target.value })} />
            </div>
          </div>
          <Muted className="text-xs">Only this session moves. Students are told; if any already has office hours at the new time, nothing moves and you'll see who.</Muted>
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryBtn} onClick={() => setMoving(null)}>Cancel</button>
            <button
              type="button"
              className={primaryBtn}
              onClick={async () => {
                if (!moving) return;
                await run(() => officeHoursApi.moveSession(moving.session_id, moveForm), "Session moved — students told");
                setMoving(null);
              }}
            >
              Move session
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={rollingOver} onClose={() => setRollingOver(false)} title="Copy to another term">
        <div className="space-y-3">
          <div>
            <label htmlFor="oh-rollover-term" className={labelCls}>Term</label>
            <SelectField id="oh-rollover-term" className={inputCls} value={targetTerm} onChange={(e) => setTargetTerm(e.target.value ? Number(e.target.value) : "")}>
              <option value="">Choose a term</option>
              {terms.map((t) => (
                <option key={t.academic_term_id} value={t.academic_term_id}>{`${t.name}${t.start_date ? ` (from ${String(t.start_date).slice(0, 10)})` : ""}`}</option>
              ))}
            </SelectField>
          </div>
          <Muted className="text-xs">Days, times, room and capacity are copied as a draft. Students are re-checked for the new term; anyone whose goal was met is left out. Nobody is told until you publish.</Muted>
          <div className="flex justify-end gap-2">
            <button type="button" className={secondaryBtn} onClick={() => setRollingOver(false)}>Cancel</button>
            <button
              type="button"
              className={primaryBtn}
              disabled={!targetTerm || !schedule}
              onClick={async () => {
                if (!targetTerm || !schedule) return;
                try {
                  const r = await officeHoursApi.rollover(schedule.schedule_id, Number(targetTerm));
                  const a = r.data.data.assignment;
                  showToast(`Draft created${a ? ` — ${a.assigned.length} student(s) carried over${a.conflicts.length ? `, ${a.conflicts.length} already taken` : ""}` : ""}`, "success");
                  setRollingOver(false);
                  navigate(`/office-hours/schedules/${r.data.data.schedule_id}`);
                } catch (e) {
                  showToast(apiError(e, "Couldn't copy"), "error");
                }
              }}
            >
              Copy as draft
            </button>
          </div>
        </div>
      </Modal>

      {registerFor && <RegisterSheet sessionId={registerFor.session_id} onClose={() => setRegisterFor(null)} onSaved={() => void load()} />}
    </div>
  );
};

export default ScheduleDetail;
